export type SiteAttributeFilterOption = {
  $group?: string;
  label?: string;
  key: string;
  value: string[];
  type?: string;
};

export type SiteAttributeSource = {
  attributes?: Array<{
    category?: string | null;
    name?: string | null;
    value?: string[] | null;
    type?: string | null;
  } | null> | null;
};

export function getAttributeFilterOptions(
  sites: SiteAttributeSource[]
): SiteAttributeFilterOption[] {
  // Use Map for O(1) lookup instead of findIndex which is O(n)
  const attributeMap = new Map<string, SiteAttributeFilterOption>();
  const valueSet = new Map<string, Set<string>>();
  sites.forEach(({ attributes }) => {
    attributes?.forEach((attribute) => {
      // Safe destructuring with null checks
      const { category, name, value, type } = attribute || {};
      // Enhanced safety checks for malformed data
      if (
        !attribute ||
        type === 'attachment' ||
        !name ||
        !value ||
        !Array.isArray(value) ||
        value.length === 0
      ) {
        return;
      }
      if (!attributeMap.has(name)) {
        attributeMap.set(name, {
          $group: category?.trimEnd(),
          label: name.trimStart(),
          key: name,
          value: [],
          type: attribute.type ?? undefined,
        });
        valueSet.set(name, new Set());
      }
      const currentValueSet = valueSet.get(name)!;
      // Use Set to avoid duplicates in O(1) time
      // Note: Contact resolution is now handled by FacetedFilterSelect when isAttributeFilter={true}
      value.forEach((v) => {
        if (v) {
          // Parse contact attribute values (stored as JSON strings like '["19"]')
          if (type === 'contact') {
            try {
              const parsed = JSON.parse(v);
              const contactIds = Array.isArray(parsed) ? parsed : [parsed];
              contactIds.forEach((contactId) => {
                if (contactId) {
                  currentValueSet.add(String(contactId));
                }
              });
            } catch {
              // Fallback: if not JSON, treat as single contact ID
              currentValueSet.add(v);
            }
          } else {
            currentValueSet.add(v);
          }
        }
      });
    });
  });
  // Convert Sets back to arrays
  const result: SiteAttributeFilterOption[] = [];
  attributeMap.forEach((filter, name) => {
    filter.value = Array.from(valueSet.get(name)!);
    result.push(filter);
  });
  return result;
}
